/**
 * Utilitaires DOMParser natifs.
 *
 * Aucune dépendance XML : le navigateur fournit DOMParser. Les recherches se
 * font sur le nom local (et non le préfixe), car XRechnung, Factur-X et UBL
 * utilisent des préfixes différents pour les mêmes espaces de noms.
 */
import { parseDecimal, type Dec } from '../money';

export function decodeXmlBytes(bytes: Uint8Array): string {
  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) {
    return new TextDecoder('utf-16le').decode(bytes);
  }
  if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) {
    return new TextDecoder('utf-16be').decode(bytes);
  }
  const start = bytes.length >= 3 && bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf ? 3 : 0;
  return new TextDecoder('utf-8').decode(bytes.subarray(start));
}

export class XmlParseError extends Error {
  readonly code: string;

  constructor(message: string, code = 'XML_INVALID') {
    super(message);
    this.name = 'XmlParseError';
    this.code = code;
  }
}

export function parseXml(bytes: Uint8Array): Document {
  if (typeof DOMParser === 'undefined') {
    throw new XmlParseError('DOMParser indisponible dans cet environnement');
  }
  const source = decodeXmlBytes(bytes);
  if (source.trim() === '') throw new XmlParseError('Document XML vide');
  const doc = new DOMParser().parseFromString(source, 'application/xml');
  if (!doc.documentElement) throw new XmlParseError('Aucun élément racine');
  const root = doc.documentElement;
  if (root.localName === 'parsererror' || root.nodeName === 'parsererror') {
    throw new XmlParseError(`XML invalide : ${root.textContent?.slice(0, 200) ?? ''}`);
  }
  return doc;
}

export type El = Element;

/** Enfants directs portant ce nom local (insensible au préfixe). */
export function children(el: Element | Document | null | undefined, local: string): Element[] {
  if (!el) return [];
  const parent = el.nodeType === 9 ? (el as Document).documentElement : (el as Element);
  const out: Element[] = [];
  for (let i = 0; i < parent.children.length; i += 1) {
    const c = parent.children[i];
    if (c && c.localName === local) out.push(c);
  }
  return out;
}

export function child(el: Element | Document | null | undefined, local: string): Element | null {
  if (!el) return null;
  const parent = el.nodeType === 9 ? (el as Document).documentElement : (el as Element);
  for (let i = 0; i < parent.children.length; i += 1) {
    const c = parent.children[i];
    if (c && c.localName === local) return c;
  }
  return null;
}

/** Descend une chaîne d'enfants directs : path(root, 'A', 'B') → A/B. */
export function path(el: Element | Document | null | undefined, ...locals: string[]): Element | null {
  let cur: Element | null = el ? (el.nodeType === 9 ? (el as Document).documentElement : (el as Element)) : null;
  for (const local of locals) {
    if (!cur) return null;
    cur = child(cur, local);
  }
  return cur;
}

/** Texte normalisé (espaces compactés) ou undefined si vide. */
export function text(el: Element | null | undefined): string | undefined {
  if (!el) return undefined;
  const raw = el.textContent ?? '';
  const clean = raw.replace(/\s+/g, ' ').trim();
  return clean === '' ? undefined : clean;
}

/** Texte d'un chemin : txt(root, 'A', 'B'). */
export function txt(el: Element | Document | null | undefined, ...locals: string[]): string | undefined {
  return text(path(el, ...locals));
}

/** Attribut par nom local (ex. unitCode, schemeID, currencyID). */
export function attr(el: Element | null | undefined, localName: string): string | undefined {
  if (!el) return undefined;
  for (let i = 0; i < el.attributes.length; i += 1) {
    const a = el.attributes[i];
    if (a && a.localName === localName) return a.value;
  }
  return undefined;
}

export function num(el: Element | null | undefined): Dec | undefined {
  return parseDecimal(text(el));
}

/** Tous les descendants portant ce nom local (document order). */
export function descendants(el: Element | Document, local: string): Element[] {
  const root = el.nodeType === 9 ? (el as Document) : (el as Element).ownerDocument ?? (el as unknown as Document);
  return Array.from(root.getElementsByTagNameNS('*', local)) as Element[];
}

/** Descendants sous un élément donné, filtrés par nom local. */
export function descendantsOf(el: Element | null | undefined, local: string): Element[] {
  if (!el) return [];
  return Array.from(el.getElementsByTagNameNS('*', local)) as Element[];
}

export function rootName(doc: Document): string {
  return doc.documentElement?.localName ?? '';
}

/** AAAA-MM-JJ depuis une date CII (format 102 = AAAAMMJJ) ou UBL (ISO). */
export function normalizeDate(value: string | undefined, format?: string): string | undefined {
  if (!value) return undefined;
  const v = value.trim();
  const iso = /^(\d{4})-(\d{2})-(\d{2})/.exec(v);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  if (format === '102' || /^\d{8}$/.test(v)) {
    const m = /^(\d{4})(\d{2})(\d{2})$/.exec(v);
    if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  }
  if (/^\d{4}-\d{2}$/.test(v)) return undefined;
  return undefined;
}

/** Date CII : <udt:DateTimeString format="102"> ou DateTime ISO direct. */
export function ciiDate(el: Element | null | undefined): string | undefined {
  if (!el) return undefined;
  const inner = child(el, 'DateTimeString');
  if (inner) return normalizeDate(text(inner), attr(inner, 'format'));
  return normalizeDate(text(el));
}
